import { useSyncExternalStore } from 'react';
import { businessDate } from '@till-erp/platform/dates';
import type { Me } from './api.ts';
import { useSession } from './session.tsx';

/** Takt, in dem Kopfdatum und Testuhr-Band clientseitig weiterlaufen. */
const TICK_MS = 30_000;

// Uhr des Browsers als kleiner Speicher: ändert sich nur im Takt (nicht bei jedem Rendern).
let browserNow = Date.now();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  browserNow = Date.now();
  timer ??= setInterval(() => {
    browserNow = Date.now();
    for (const l of listeners) l();
  }, TICK_MS);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

const getBrowserNow = () => browserNow;

// Versatz Testuhr ↔ Browser je geladenem Stand von /me (gleiches Objekt = gleicher Versatz).
const offsets = new WeakMap<NonNullable<Me['testClock']>, number>();

/**
 * Beim Übernehmen einer Antwort von /me aufrufen (nicht beim Rendern): merkt den Versatz der Testuhr zur
 * Browser-Uhr und frischt die Browser-Uhr auf – sonst zeigte das Band nach dem Stellen bis zu einem Takt zu früh.
 */
export function noteMeReceived(me: Me): void {
  if (!me.testClock?.active) return;
  browserNow = Date.now();
  offsets.set(me.testClock, Date.parse(me.testClock.now) - browserNow);
  for (const l of listeners) l();
}

/** Nur lesen (rein im Rendern); ohne gemerkten Versatz gilt der zuletzt bekannte Stand der Browser-Uhr. */
function offsetOf(testClock: Me['testClock']): number {
  if (!testClock?.active) return 0;
  return offsets.get(testClock) ?? Date.parse(testClock.now) - browserNow;
}

/**
 * Fachliches Jetzt für die Oberfläche (Story 1.17): mit gestellter Testuhr die Uhr des Browsers plus den
 * Versatz aus `/me.testClock.now`, sonst die Uhr des Browsers. Läuft im 30-s-Takt weiter (D1).
 */
export function useBusinessNow(): { now: Date; testActive: boolean } {
  const { me } = useSession();
  const offset = offsetOf(me.testClock);
  const nowMs = useSyncExternalStore(subscribe, getBrowserNow);
  return { now: new Date(nowMs + offset), testActive: me.testClock?.active ?? false };
}

/** Heutiger Kalendertag (ISO, Europe/Berlin) aus dem fachlichen Jetzt – z. B. `today` für `DateField` (Ticket 1.215). */
export function useToday(): string {
  return businessDate(useBusinessNow().now);
}
